
import { memo } from "react";
import { useTranslations } from "use-intl";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { Loader2 } from "lucide-react";
import { getStepTypeConfig } from "./step-constants";
import { SubtypeIcon } from "@/features/connectors/components/connector-constants";
import type { StepNodeData } from "./flow.utils";
import { getStepSummary } from "./summaries/getStepSummary";
import { AGENTIC_STEP_TYPES } from "@/types/workflow.types";
import { AgenticNodeFrame } from "../agentic/agentic-node-frame";

function StepNodeComponent({ data }: NodeProps) {
  const t = useTranslations("workflows");
  const nodeData = data as unknown as StepNodeData;
  const { step, connectorName, connectorSubtype } = nodeData;
  const liveOverlay = nodeData.liveOverlay;
  const config = getStepTypeConfig(step.stepType);
  const Icon = config.icon;
  const isAgentic = AGENTIC_STEP_TYPES.has(step.stepType);

  const summaryItems = getStepSummary(step.stepType, step.config, t as (key: string, values?: Record<string, string | number>) => string);

  const nodeBody = (
    <div
      className={`w-[320px] rounded-xl border-l-4 bg-white shadow-sm transition-shadow hover:shadow-md dark:bg-gray-800 ${liveOverlay?.isActive ? "ring-2 ring-emerald-400/70" : ""} ${config.borderColor}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-3 py-2.5">
        <div
          className={`flex size-8 shrink-0 items-center justify-center rounded-lg ${config.bgColor} ${config.darkBgColor}`}
        >
          {liveOverlay?.isActive ? (
            <Loader2 className="size-4 animate-spin text-emerald-600 dark:text-emerald-300" />
          ) : (
            <Icon className={`size-4 ${config.iconColor}`} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[9px] font-bold text-gray-500 dark:bg-gray-700 dark:text-gray-400">
              {step.stepOrder}
            </span>
            <h4 className="truncate text-xs font-semibold text-gray-900 dark:text-white">
              {step.stepName}
            </h4>
          </div>
          <div className="mt-0.5 flex items-center gap-1.5">
            <span className={`text-[10px] font-medium ${config.iconColor}`}>
              {config.label}
            </span>
            {connectorName && connectorSubtype && (
              <>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <div className="flex items-center gap-1 truncate">
                  <SubtypeIcon subtype={connectorSubtype} size="sm" />
                  <span className="truncate text-[10px] text-gray-500 dark:text-gray-400">
                    {connectorName}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Summary — all step types routed through getStepSummary dispatcher */}
      {summaryItems && summaryItems.length > 0 ? (
        <div className="nopan nodrag border-t border-gray-100 px-3 py-2.5 dark:border-gray-700/50">
          <div className="space-y-2.5">{summaryItems}</div>
        </div>
      ) : null}

      {liveOverlay && (
        <div className="nopan nodrag border-t border-emerald-100 bg-emerald-50/70 px-3 py-2 dark:border-emerald-900/60 dark:bg-emerald-950/20">
          <div className="flex items-center justify-between gap-2 text-[10px] font-medium">
            <span className="capitalize text-emerald-700 dark:text-emerald-300">
              {t(`run_status.${liveOverlay.status}` as Parameters<typeof t>[0])}
            </span>
            <span className="font-mono text-gray-500 dark:text-gray-400">
              {Math.round(liveOverlay.progressPercent)}%
            </span>
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white dark:bg-gray-900">
            <div
              className="h-full rounded-full bg-emerald-500 transition-all duration-500"
              style={{ width: `${Math.min(100, Math.max(0, liveOverlay.progressPercent))}%` }}
            />
          </div>
          {(liveOverlay.progressMessage || liveOverlay.liveTextPreview) && (
            <p className="mt-1.5 line-clamp-2 text-[10px] leading-4 text-gray-600 dark:text-gray-300">
              {liveOverlay.liveTextPreview || liveOverlay.progressMessage}
            </p>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <Handle type="target" position={Position.Left} className="!bg-gray-400" />
      {/* Dashed border + gradient signals "agent step" at-a-glance per design
          system token map; no semantic-status overlap with status-color border. */}
      <AgenticNodeFrame variant={isAgentic ? "agentic" : "etl"}>
        {nodeBody}
      </AgenticNodeFrame>
      <Handle type="source" position={Position.Right} className="!bg-gray-400" />
    </>
  );
}

export default memo(StepNodeComponent);
